import { Box, Image, type BoxStyle } from '../../../../../../mads';

import { colors } from '@indeed/ifl-tokens';
import React, { type PropsWithChildren } from 'react';
import { StyleSheet } from 'react-native';

export type LogoProps = {
    /**
     * The URL of the Logo IMG.
     */
    url: string;
    /**
     * The accessibility text of the img for screen readers.
     */
    altText: string;
    /**
     * The size of the logo.
     */
    size: 32 | 60 | 64;
    /**
     * Whether the logo should be surrounded with a shadow, making it look like it is elevated from the page.
     */
    hasShadow: boolean;
    /**
     * Whether the logo should be surrounded with a border.
     */
    hasBorder: boolean;
};

/**
 * Used to render a square logo.
 */
export const Logo = (props: LogoProps) => {
    let sizeStyle;
    switch (props.size) {
        case 32:
            sizeStyle = styles.small;
            break;
        case 60:
            sizeStyle = styles.medium;
            break;
        case 64:
            sizeStyle = styles.large;
            break;
    }

    return (
        <LogoShadow style={sizeStyle} enabled={props.hasShadow}>
            <Image
                source={{ uri: props.url }}
                style={[sizeStyle, props.hasBorder && styles.border]}
                accessibilityLabel={props.altText}
                testID="company-logo"
            />
        </LogoShadow>
    );
};

type LogoShadowProps = {
    enabled: boolean;
    style: BoxStyle;
};

const LogoShadow = (props: PropsWithChildren<LogoShadowProps>) => {
    if (!props.enabled) {
        return props.children;
    }

    return <Box sx={[styles.logoShadow, props.style]}>{props.children}</Box>;
};

const styles = StyleSheet.create({
    small: {
        width: 32,
        height: 32,
        borderRadius: 8,
    },
    medium: {
        width: 60,
        height: 60,
        borderRadius: 8,
    },
    large: {
        width: 64,
        height: 64,
        borderRadius: 12,
    },
    logoShadow: {
        // shadow
        borderColor: '#ffffff',
        borderRadius: 8,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.4,
        shadowRadius: 4,
        elevation: 10,
    },
    border: {
        borderColor: colors.background['[base]'],
        borderWidth: 2,
    },
});
